import React from "react"

import { Button } from "../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination"

const TWO_LINE_CLAMP_STYLE = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden"
}

const ONE_LINE_ELLIPSIS_STYLE = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
}

function tx(i18n, key, fallback = "") {
  const value = i18n?.[key]
  if (typeof value !== "string" || value.length === 0) return fallback
  return value
}

function fmt(template, replacements = {}) {
  return Object.entries(replacements).reduce((acc, [key, value]) => {
    return acc.replaceAll(`%{${key}}`, String(value))
  }, String(template || ""))
}

export function CoffeeLibrary({ coffees = [], pagination = {}, links = {}, scope = "active", counts = {}, i18n = {} }) {
  const currentScope = scope || "active"
  const activeCount = counts.active ?? 0
  const archivedCount = counts.archived ?? 0
  const isArchivedScope = currentScope === "archived"
  const prevLabel = tx(i18n, "pagination_prev", "Previous")
  const nextLabel = tx(i18n, "pagination_next", "Next")
  const morePagesLabel = tx(i18n, "pagination_more_pages", "More pages")

  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-semibold tracking-tight">{tx(i18n, "title", "커피 라이브러리")}</h1>
        <Button asChild>
          <a href={links.new_coffee_path}>{tx(i18n, "new_cta", "원두 등록")}</a>
        </Button>
      </div>

      <div className="mt-3 inline-flex min-h-11 items-center gap-1 rounded-xl border border-border bg-muted/40 p-1">
        <a
          href={links.active_path || "#"}
          className={`inline-flex min-h-9 items-center justify-center rounded-lg px-4 text-sm font-medium transition-colors ${
            !isArchivedScope ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {fmt(tx(i18n, "scope_active", "사용 중 (%{count})"), { count: activeCount })}
        </a>
        <a
          href={links.archived_path || "#"}
          className={`inline-flex min-h-9 items-center justify-center rounded-lg px-4 text-sm font-medium transition-colors ${
            isArchivedScope ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {fmt(tx(i18n, "scope_archived", "소진 (%{count})"), { count: archivedCount })}
        </a>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{tx(i18n, "scope_help", "다 쓴 원두를 소진으로 옮기면 목록에서 숨겨집니다. 기록은 그대로 남습니다.")}</p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {coffees.map((coffee) => (
          <Card key={coffee.id} className="h-full overflow-hidden">
            <div className="flex h-full flex-col">
              <CardHeader className="gap-1 px-4 pb-2 pt-3">
                <div className="flex items-center justify-between gap-2">
                  <CardDescription className="truncate text-[11px] uppercase tracking-wide leading-4">
                    {coffee.roaster}
                  </CardDescription>
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                      coffee.inventory_status === "archived"
                        ? "border-rule bg-recess text-ink-dim"
                        : "border-ink-dim/40 bg-sheet text-ink"
                    }`}
                  >
                    {coffee.inventory_status === "archived"
                      ? tx(i18n, "status_archived", "소진")
                      : tx(i18n, "status_active", "사용 중")}
                  </span>
                </div>
                <CardTitle className="truncate text-base leading-tight">
                  <a href={coffee.show_path} className="block truncate hover:underline">
                    {coffee.name}
                  </a>
                </CardTitle>
                <CardDescription className="text-xs leading-4" style={ONE_LINE_ELLIPSIS_STYLE}>
                  {coffee.meta || tx(i18n, "meta_fallback", "세부 정보 없음")}
                </CardDescription>
                <div className="min-h-8 pt-1">
                  <p className="text-xs leading-4 text-muted-foreground" style={TWO_LINE_CLAMP_STYLE}>
                    {coffee.introduction_excerpt || tx(i18n, "introduction_fallback", "설명 없음")}
                  </p>
                </div>
              </CardHeader>
              <CardContent className="mt-auto space-y-3 px-4 pb-4 pt-1">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="ui-card-muted">
                    <p className="text-muted-foreground">{tx(i18n, "metric_brews", "Brews")}</p>
                    <p className="font-semibold text-foreground">{coffee.total_brews_count}</p>
                  </div>
                  <div className="ui-card-muted">
                    <p className="text-muted-foreground">{tx(i18n, "metric_guest", "Guest")}</p>
                    <p className="font-semibold text-foreground">{coffee.total_guest_evaluations_count}</p>
                  </div>
                  <div className="ui-card-muted">
                    <p className="text-muted-foreground">{tx(i18n, "metric_last_brew", "Last Brew")}</p>
                    <p className="font-semibold text-foreground">{coffee.last_brewed_at || "-"}</p>
                  </div>
                  <div className="ui-card-muted">
                    <p className="text-muted-foreground">{tx(i18n, "metric_avg_tds", "Avg TDS")}</p>
                    <p className="font-semibold text-foreground">{coffee.average_tds || "-"}</p>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  {fmt(tx(i18n, "registered_at", "등록일 %{date}"), { date: coffee.registered_at || "-" })}
                </p>

                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <a href={coffee.edit_path}>{tx(i18n, "action_edit", "수정")}</a>
                  </Button>
                  {coffee.inventory_status === "archived" ? (
                    <Button variant="outline" size="sm" asChild>
                      <a href={coffee.unarchive_path} data-turbo-method="patch">
                        {tx(i18n, "action_restore", "다시 사용하기")}
                      </a>
                    </Button>
                  ) : (
                    <Button variant="secondary" size="sm" asChild>
                      <a href={coffee.new_brew_path}>{tx(i18n, "action_brew", "브루잉")}</a>
                    </Button>
                  )}
                  <Button size="sm" asChild>
                    <a href={coffee.qr_session_path} data-turbo-method="post">
                      {tx(i18n, "action_qr_session", "QR 세션")}
                    </a>
                  </Button>
                  {coffee.inventory_status === "archived" ? (
                    <Button variant="outline" size="sm" className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive" asChild>
                      <a
                        href={coffee.delete_path}
                        data-turbo-method="delete"
                        data-turbo-confirm={tx(i18n, "confirm_delete", "이 원두를 삭제하시겠습니까? 관련 브루/세션 데이터도 함께 삭제됩니다.")}
                      >
                        {tx(i18n, "action_delete", "삭제")}
                      </a>
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" asChild>
                      <a href={coffee.archive_path} data-turbo-method="patch">
                        {tx(i18n, "action_archive", "소진으로 옮기기")}
                      </a>
                    </Button>
                  )}
                </div>
              </CardContent>
            </div>
          </Card>
        ))}
      </div>

      {pagination.total_pages > 1 && (
        <div className="mt-6 space-y-2">
          <p className="text-center text-xs text-muted-foreground">
            {fmt(tx(i18n, "pagination_summary", "%{current} / %{total} 페이지 (총 %{count}개)"), {
              current: pagination.current_page,
              total: pagination.total_pages,
              count: pagination.total_count,
            })}
          </p>

          <Pagination>
            <PaginationContent>
              <PaginationItem>
                {pagination.prev_path ? (
                  <PaginationPrevious href={pagination.prev_path} label={prevLabel} ariaLabel={prevLabel} />
                ) : (
                  <PaginationPrevious
                    href="#"
                    label={prevLabel}
                    ariaLabel={prevLabel}
                    aria-disabled="true"
                    onClick={(event) => event.preventDefault()}
                    className="pointer-events-none opacity-50"
                  />
                )}
              </PaginationItem>

              {(pagination.items || []).map((item, index) => (
                <PaginationItem key={`${item.kind}-${item.page || index}`}>
                  {item.kind === "ellipsis" ? (
                    <PaginationEllipsis srLabel={morePagesLabel} />
                  ) : (
                    <PaginationLink href={item.path} isActive={item.current}>
                      {item.page}
                    </PaginationLink>
                  )}
                </PaginationItem>
              ))}

              <PaginationItem>
                {pagination.next_path ? (
                  <PaginationNext href={pagination.next_path} label={nextLabel} ariaLabel={nextLabel} />
                ) : (
                  <PaginationNext
                    href="#"
                    label={nextLabel}
                    ariaLabel={nextLabel}
                    aria-disabled="true"
                    onClick={(event) => event.preventDefault()}
                    className="pointer-events-none opacity-50"
                  />
                )}
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </>
  )
}
